/* rules.js — Évalue un profil contre le modèle normalisé. Par règle :
 * pass | warning | error | unevaluable, ou not-applicable si sa condition
 * (when) est fausse. Un champ absent rend la règle non évaluable, sauf
 * exists/absent. Verdict global à quatre états. Expose self.MediaRules. */
'use strict';

import K from './findings.js';

var ENGINE_VERSION = '1.3';


var VERDICT = {
  CONFORME:      { code: 'conforme',      label: 'Conforme au profil sélectionné' },
  AVERTISSEMENT: { code: 'conforme-avert', label: 'Conforme, avec avertissements' },
  NON_CONFORME:  { code: 'non-conforme',  label: 'Non conforme au profil sélectionné' },
  INDETERMINE:   { code: 'indeterminate', label: 'Impossible à déterminer' }
};

/* ---------- Accès aux champs ---------- */
function getPath(obj, path) {
  if (obj == null || path == null) return null;
  var parts = String(path).split('.');
  var cur = obj;
  for (var i = 0; i < parts.length; i++) {
    if (cur == null) return null;
    cur = cur[parts[i]];
  }
  return cur == null ? null : cur;
}

function toNum(v) {
  if (typeof v === 'number') return v;
  if (v == null) return NaN;
  var n = parseFloat(String(v).replace(',', '.'));
  return n;
}

function valEq(a, b) {
  if (typeof a === 'number' || typeof b === 'number') {
    var na = toNum(a), nb = toNum(b);
    if (!isNaN(na) && !isNaN(nb)) return na === nb;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') return a === b;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

/* ---------- Sélecteur : restreindre aux pistes concernées ---------- */
function matchesSelector(t, sel) {
  if (!sel) return true;
  if (sel.language != null && (!t.language || t.language.code !== sel.language)) return false;
  if (sel.default != null && t.default !== sel.default) return false;
  if (sel.forced != null && t.forced !== sel.forced) return false;
  if (sel.index != null && (t.ordinal - 1) !== sel.index) return false;
  return true;
}

// Valeur déclarée (modèle) ou mesurée (model.measured), selon `origin`.
function valueOf(model, track, field, origin) {
  if (origin !== 'measured') return getPath(track, field);
  var subject = track && track.kind ? track : null;
  var list = (model && model.measured) || [];
  for (var i = 0; i < list.length; i++) {
    var v = list[i];
    var same = subject ? (v.subject.kind === subject.kind && v.subject.ordinal === subject.ordinal) : v.subject.kind === 'container';
    if (same && v.property === field) return v.value == null ? null : v.value;
  }
  return null;
}

/* ---------- Assertion sur UNE piste : 'pass' | 'fail' | 'uneval' ---------- */
function assertOne(track, rule, model) {
  var f = valueOf(model, track, rule.field, rule.origin);
  var op = rule.op, val = rule.value;

  if (op === 'exists') return f != null ? 'pass' : 'fail';
  if (op === 'absent') return f == null ? 'pass' : 'fail';

  if (f == null) return 'uneval'; // donnée absente → non évaluable (jamais pass/fail)

  switch (op) {
    case 'eq':  return valEq(f, val) ? 'pass' : 'fail';
    case 'ne':  return !valEq(f, val) ? 'pass' : 'fail';
    case 'gt':  return toNum(f) >  toNum(val) ? 'pass' : 'fail';
    case 'gte': return toNum(f) >= toNum(val) ? 'pass' : 'fail';
    case 'lt':  return toNum(f) <  toNum(val) ? 'pass' : 'fail';
    case 'lte': return toNum(f) <= toNum(val) ? 'pass' : 'fail';
    case 'contains': return String(f).toLowerCase().indexOf(String(val).toLowerCase()) >= 0 ? 'pass' : 'fail';
    case 'notContains': return String(f).toLowerCase().indexOf(String(val).toLowerCase()) < 0 ? 'pass' : 'fail';
    case 'inList': return (val || []).some(function (v) { return valEq(f, v); }) ? 'pass' : 'fail';
    case 'range': return (toNum(f) >= toNum(val.min) && toNum(f) <= toNum(val.max)) ? 'pass' : 'fail';
    case 'tolerance': return Math.abs(toNum(f) - toNum(val.target)) <= toNum(val.tol) ? 'pass' : 'fail';
    case 'ratio': { // champ ≈ num / den (deux champs de la même piste), écart relatif ≤ tol
      var n = toNum(getPath(track, val.num)), d = toNum(getPath(track, val.den));
      if (isNaN(n) || isNaN(d) || d === 0) return 'uneval';
      var q = n / d;
      return Math.abs(toNum(f) - q) / q <= toNum(val.tol) ? 'pass' : 'fail';
    }
    default: return 'uneval'; // opérateur inconnu → prudence
  }
}

// Une condition, ou une liste dont toutes doivent tenir.
function assertWhen(track, when, model) {
  var list = Array.isArray(when) ? when : [when];
  var uneval = false;
  for (var i = 0; i < list.length; i++) {
    var r = assertOne(track, list[i], model);
    if (r === 'fail') return 'fail';
    if (r === 'uneval') uneval = true;
  }
  return uneval ? 'uneval' : 'pass';
}

/* ---------- Quantificateur : agrège les résultats par piste ---------- */
function aggregate(perTrack, quantifier, count) {
  var passN = 0, failN = 0, unevalN = 0;
  perTrack.forEach(function (r) { if (r === 'pass') passN++; else if (r === 'fail') failN++; else unevalN++; });

  switch (quantifier) {
    case 'all':  return failN > 0 ? 'fail' : (unevalN > 0 ? 'uneval' : 'pass');
    case 'any':  return passN > 0 ? 'pass' : (unevalN > 0 ? 'uneval' : 'fail');
    case 'none': return passN > 0 ? 'fail' : (unevalN > 0 ? 'uneval' : 'pass');
    case 'atLeast': return passN >= count ? 'pass' : ((passN + unevalN) >= count ? 'uneval' : 'fail');
    case 'atMost':  return passN >  count ? 'fail' : ((passN + unevalN) >  count ? 'uneval' : 'pass');
    case 'exactly':
      if (passN === count && unevalN === 0) return 'pass';
      if (passN > count) return 'fail';
      if (passN <= count && count <= passN + unevalN) return 'uneval';
      return 'fail';
    default: return failN > 0 ? 'fail' : (unevalN > 0 ? 'uneval' : 'pass');
  }
}

// Le conteneur plus « counts.video », « counts.audio »… pour exists/absent.
function containerView(model) {
  var v = Object.create(model.container);
  v.counts = model.counts || {};
  return v;
}

/* ---------- Évaluation d'une règle ---------- */
function evalRule(rule, model) {
  var severity = rule.severity === 'error' ? 'error' : 'warning';
  var candidates;
  if (rule.track === 'container') candidates = [containerView(model)];
  else candidates = model.tracks.filter(function (t) {
    return t.kind === rule.track && matchesSelector(t, rule.selector);
  });

  // seules comptent les pistes dont la condition est vraie
  var cond = null;
  if (rule.when) {
    cond = candidates.map(function (t) { return assertWhen(t, rule.when, model); });
    candidates = candidates.filter(function (t, i) { return cond[i] !== 'fail'; });
    cond = cond.filter(function (c) { return c !== 'fail'; });
  }
  var perTrack = candidates.map(function (t, i) {
    return cond && cond[i] === 'uneval' ? 'uneval' : assertOne(t, rule, model);
  });
  var outcome = aggregate(perTrack, rule.quantifier || 'all', rule.count);

  var status = outcome === 'pass' ? 'pass' : (outcome === 'uneval' ? 'unevaluable' : severity);
  if (rule.when && !candidates.length) status = 'not-applicable';

  var observed = candidates.map(function (t) { var v = valueOf(model, t, rule.field, rule.origin); return v == null ? '—' : v; });

  var observedValues = candidates.map(function (t) {
    var ov = K.value(K.subjectOf(t), rule.field, valueOf(model, t, rule.field, rule.origin));
    if (rule.origin === 'measured') { ov.origin = 'measured'; ov.source = 'structure'; }
    return ov;
  });
  var failing = candidates.filter(function (t, i) { return perTrack[i] !== 'pass'; });
  var subject = { kind: 'container' };
  if (rule.track !== 'container') {
    if (candidates.length === 1) subject = K.subjectOf(candidates[0]);
    else if (failing.length === 1) subject = K.subjectOf(failing[0]);
  }

  return {
    id: rule.id || rule.field,
    message: rule.message || (rule.field + ' ' + (rule.op || '') + ' ' + fmtVal(rule.value)),
    field: rule.field,
    track: rule.track,
    severity: severity,
    status: status,                 // pass | warning | error | unevaluable
    candidates: candidates.length,
    observed: observed,
    expected: K.expected(rule),
    observedValues: observedValues,
    subject: subject,
    failing: failing.map(function (t) { return K.subjectOf(t); })
  };
}

function fmtVal(v) {
  if (v == null) return '';
  if (Array.isArray(v)) return '[' + v.join(', ') + ']';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/* ---------- Périmètre d'application ---------- */
function checkApplicable(profile, model) {
  var at = profile.appliesTo || {};
  var req = at.requireTracks || {};
  var missing = [];
  Object.keys(req).forEach(function (k) {
    if ((model.counts[k] || 0) < req[k]) missing.push(req[k] + ' × ' + k);
  });
  if (missing.length) {
    return { ok: false, reason: 'Ce profil requiert ' + missing.join(', ') + ', absent(s) de ce fichier.' };
  }
  return { ok: true };
}

/* ---------- Verdict global : calcul EXPLICITE ---------- */
function computeVerdict(applicable, results) {
  if (!applicable.ok) return VERDICT.INDETERMINE;
  // une erreur avérée l'emporte
  if (results.some(function (r) { return r.status === 'error'; })) return VERDICT.NON_CONFORME;
  // une règle décisive non évaluable : rien ne peut être conclu
  if (results.some(function (r) { return r.status === 'unevaluable' && r.severity === 'error'; })) return VERDICT.INDETERMINE;
  if (results.some(function (r) { return r.status === 'warning'; })) return VERDICT.AVERTISSEMENT;
  return VERDICT.CONFORME;
}

function evaluate(profile, model) {
  var applicable = checkApplicable(profile, model);
  var results = applicable.ok ? (profile.rules || []).map(function (r) { return evalRule(r, model); }) : [];
  var verdict = computeVerdict(applicable, results);
  return {
    profile: { id: profile.id, version: profile.version, date: profile.date, name: profile.name, statut: profile.statut,
      family: profile.family, description: profile.description, notChecked: profile.notChecked, sources: profile.sources },
    engine: ENGINE_VERSION,
    applicable: applicable,
    verdict: verdict,
    counts: {
      pass: results.filter(function (r) { return r.status === 'pass'; }).length,
      warning: results.filter(function (r) { return r.status === 'warning'; }).length,
      error: results.filter(function (r) { return r.status === 'error'; }).length,
      unevaluable: results.filter(function (r) { return r.status === 'unevaluable'; }).length,
      notApplicable: results.filter(function (r) { return r.status === 'not-applicable'; }).length
    },
    results: results
  };
}

function validateProfile(p) {
  if (!p || typeof p !== 'object') return 'Profil illisible.';
  if (!p.id) return 'Champ « id » manquant.';
  if (!p.name) return 'Champ « name » manquant.';
  if (!Array.isArray(p.rules)) return 'Champ « rules » manquant ou invalide.';
  for (var i = 0; i < p.rules.length; i++) {
    var r = p.rules[i], where = 'Règle ' + (r && r.id ? '« ' + r.id + ' »' : 'n°' + (i + 1)) + ' : ';
    if (!r) return where + 'règle vide.';
    var conds = r.when == null ? [] : (Array.isArray(r.when) ? r.when : [r.when]);
    for (var c = 0; c < conds.length; c++) {
      if (!conds[c] || !conds[c].field || !conds[c].op) return where + 'condition « when » incomplète (« field » et « op »).';
    }
    if (r.op === 'ratio' && !(r.value && r.value.num && r.value.den && r.value.tol != null)) return where + 'l’opérateur « ratio » attend { num, den, tol }.';
    if (r.origin != null && r.origin !== 'declared' && r.origin !== 'measured') return where + 'origine « declared » ou « measured » attendue.';
  }
  if (p.family != null && !FAMILIES[p.family]) return 'Famille inconnue : ' + p.family + '.';
  if (p.notChecked != null && !Array.isArray(p.notChecked)) return 'Champ « notChecked » : liste de phrases attendue.';
  return null; // ok
}

// Familles, dans l'ordre proposé. Un profil sans famille reste accepté.
var FAMILIES = {
  lecture: 'Appareils et lecteurs',
  diffusion: 'Plateformes de diffusion',
  production: 'Masters de production',
  archivage: 'Archivage'
};

var api = {
  ENGINE_VERSION: ENGINE_VERSION, VERDICT: VERDICT, FAMILIES: FAMILIES,
  evaluate: evaluate, validateProfile: validateProfile,
  _internal: { assertOne: assertOne, aggregate: aggregate, evalRule: evalRule, computeVerdict: computeVerdict }
};
globalThis.MediaRules = api;
export default api;
